import { useAuth } from "hooks/useAuth"
import { SidebarLayout } from "layouts/SidebarLayout"
import { UpdatesPage } from "pages/UpdatesPage"
import { HomePage } from "pages/HomePage"
import { InvitationsPage } from "pages/InvitationsPage"
import { LoginPage } from "pages/LoginPage"
import { LogoutPage } from "pages/LogoutPage"
import { NewTimeCapsulePage } from "pages/NewTimeCapsulePage"
import { NotFoundPage } from "pages/NotFoundPage"
import { PrivacyPolicyPage } from "pages/PrivacyPolicyPage"
import { ProfilePage } from "pages/ProfilePage"
import { ResetPasswordPage } from "pages/ResetPasswordPage"
import { SignupPage } from "pages/SignupPage"
import { TermsPage } from "pages/TermsPage"
import { TimeCapsulePage } from "pages/TimeCapsulePage"
import { TimeCapsuleSettingsPage } from "pages/TimeCapsuleSettingsPage"
import { VerifyEmailPage } from "pages/VerifyEmailPage"
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  Outlet
} from "react-router-dom"
import { MemberTimeCapsuleSettingsPage } from "pages/MemberTimeCapsuleSettingsPage"
import { TutorialPage } from "pages/TutorialPage"
import { MemberTimeCapsulePage } from "pages/MemberTimeCapsulePage"
import { ForgotPasswordPage } from "pages/ForgotPasswordPage"

// Protected Route wrapper component
const ProtectedRoute = () => {
  const { isAuthenticated, isInitializing } = useAuth()
  const location = useLocation()

  if (isInitializing) {
    return <div>Loading...</div> // todo loading component
  }

  if (!isAuthenticated) {
    // Redirect to login but save the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <Outlet />
}

// Public Route wrapper for login/signup pages
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth()

  // Redirect to dashboard if already logged in
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

const SidebarLayoutWrapper = () => {
  return (
    <SidebarLayout>
      <Outlet />
    </SidebarLayout>
  )
}

// Main Router component
export const AppRouter = () => {
  return (
    <Router>
      <main>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          <Route
            path="/signup"
            element={
              <PublicRoute>
                <SignupPage />
              </PublicRoute>
            }
          />

          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/logout" element={<LogoutPage />} />
            {/* Sidebar routes */}
            <Route element={<SidebarLayoutWrapper />}>
              <Route path="/dashboard" element={<TutorialPage />} />
              <Route path="/latest" element={<UpdatesPage />} />
              <Route path="/invites" element={<InvitationsPage />} />
              <Route path="/time-capsules/:slug" element={<TimeCapsulePage />} />
              <Route path="/new/time-capsule" element={<NewTimeCapsulePage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route
                path="/time-capsules/:slug/settings"
                element={<TimeCapsuleSettingsPage />}
              />
              <Route
                path="/time-capsules/shared/:creatorId/:slug"
                element={<MemberTimeCapsulePage />}
              />
              <Route
                path="/time-capsules/shared/:creatorId/:slug/settings"
                element={<MemberTimeCapsuleSettingsPage />}
              />
            </Route>
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
    </Router>
  )
}
